import { createClient } from '@supabase/supabase-js';
import { logger } from '../utils/logger';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

let supabase = null;

if (!supabaseUrl || !supabaseAnonKey) {
  const error = new Error('Missing Supabase environment variables. Please check your .env file for VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY');
  logger?.error('Supabase initialization failed', error);
  // Don't throw - allow app to load with degraded functionality
  console.error('⚠️ Supabase not configured. Some features will be unavailable.');
} else {
  try {
    supabase = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        flowType: 'pkce',
        // Session storage configuration
        storage: {
          getItem: (key) => {
            if (typeof window !== 'undefined') {
              return window.localStorage?.getItem(key);
            }
            return null;
          },
          setItem: (key, value) => {
            if (typeof window !== 'undefined') {
              window.localStorage?.setItem(key, value);
            }
          },
          removeItem: (key) => {
            if (typeof window !== 'undefined') {
              window.localStorage?.removeItem(key);
            }
          }
        }
      },
      global: {
        headers: {
          'X-Client-Info': 'mybodytoday-web'
        }
      }
    });

    // Add request/response interceptor for logging
    if (supabase) {
      const originalFrom = supabase?.from?.bind(supabase);
      supabase.from = (table) => {
        const query = originalFrom(table);
        const originalSelect = query?.select?.bind(query);
        const originalInsert = query?.insert?.bind(query);
        const originalUpdate = query?.update?.bind(query);
        const originalDelete = query?.delete?.bind(query);

        query.select = (...args) => {
          logger?.debug('Supabase query', { operation: 'SELECT', table });
          return originalSelect(...args);
        };

        query.insert = (...args) => {
          logger?.debug('Supabase query', { operation: 'INSERT', table });
          return originalInsert(...args);
        };

        query.update = (...args) => {
          logger?.debug('Supabase query', { operation: 'UPDATE', table });
          return originalUpdate(...args);
        };

        query.delete = (...args) => {
          logger?.debug('Supabase query', { operation: 'DELETE', table });
          return originalDelete(...args);
        };

        return query;
      };
    }
  } catch (error) {
    logger?.error('Supabase initialization failed', error);
    // Don't throw - allow app to load with degraded functionality
    console.error('⚠️ Supabase not configured. Some features will be unavailable.');
  }
}

logger?.info('Supabase client initialized');

export { supabase };